import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Users, RefreshCw, Loader2 } from 'lucide-react'
import { usePreviewAudienceFilter } from '@/hooks/useWACampaigns'
import { useWABranches } from '@/hooks/useWAAudiences'
import type { WAAudienceFilter, WAAudienceContact } from '@/lib/api-client'

interface AudienceSelectorProps {
  value: WAAudienceFilter
  onChange: (filter: WAAudienceFilter) => void
  className?: string
}

export function AudienceSelector({ value, onChange, className }: AudienceSelectorProps) {
  const { data: branchData } = useWABranches()
  const branchesList = branchData?.data ?? []
  const previewMutation = usePreviewAudienceFilter()
  const [preview, setPreview] = React.useState<{
    totalCount: number
    sampleContacts: WAAudienceContact[]
  } | null>(null)

  // Debounce filter changes for preview
  const previewTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>()

  const triggerPreview = React.useCallback((filter: WAAudienceFilter) => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current)
    }
    previewTimeoutRef.current = setTimeout(() => {
      previewMutation.mutate(filter, {
        onSuccess: (data) => setPreview(data),
      })
    }, 500)
  }, [previewMutation])

  // Initial preview
  React.useEffect(() => {
    triggerPreview(value)
  }, []) // Only on mount

  const handleFilterChange = (updates: Partial<WAAudienceFilter>) => {
    const newFilter = { ...value, ...updates }
    // Remove undefined/null values
    Object.keys(newFilter).forEach((key) => {
      const k = key as keyof WAAudienceFilter
      if (newFilter[k] === undefined || newFilter[k] === null ||
          (Array.isArray(newFilter[k]) && (newFilter[k] as unknown[]).length === 0)) {
        delete newFilter[k]
      }
    })
    onChange(newFilter)
    triggerPreview(newFilter)
  }

  const handleBranchToggle = (branchId: number, checked: boolean) => {
    const current = value.branchIds || []
    const newBranches = checked
      ? [...current, branchId]
      : current.filter((id) => id !== branchId)
    handleFilterChange({ branchIds: newBranches.length > 0 ? newBranches : undefined })
  }

  const handleRefreshPreview = () => {
    previewMutation.mutate(value, {
      onSuccess: (data) => setPreview(data),
    })
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Branch Selection */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Branches</Label>
        <div className="grid grid-cols-2 gap-2">
          {branchesList.map((branch) => (
            <div
              key={branch.id}
              className="flex items-center space-x-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={`branch-${branch.id}`}
                checked={(value.branchIds || []).includes(branch.id)}
                onCheckedChange={(checked) => handleBranchToggle(branch.id, checked as boolean)}
              />
              <label
                htmlFor={`branch-${branch.id}`}
                className="text-sm text-text-dark cursor-pointer flex-1"
              >
                {branch.name}
              </label>
              <span className="text-[10px] text-text-muted">{branch.code}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-text-muted mt-2">
          Leave empty for all branches
        </p>
      </div>

      {/* Activity Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="inactiveDays" className="text-sm font-medium mb-2 block">
            Inactive (Min Days)
          </Label>
          <Input
            id="inactiveDays"
            type="number"
            min={0}
            placeholder="e.g. 30"
            value={value.inactiveDays || ''}
            onChange={(e) => handleFilterChange({
              inactiveDays: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
          <p className="text-[10px] text-text-muted mt-1">
            Patients inactive for X days
          </p>
        </div>
        <div>
          <Label htmlFor="maxInactiveDays" className="text-sm font-medium mb-2 block">
            Inactive (Max Days)
          </Label>
          <Input
            id="maxInactiveDays"
            type="number"
            min={0}
            placeholder="e.g. 90"
            value={value.maxInactiveDays || ''}
            onChange={(e) => handleFilterChange({
              maxInactiveDays: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
          <p className="text-[10px] text-text-muted mt-1">
            Limit inactivity range
          </p>
        </div>
      </div>

      {/* Visit Date Range Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="visitedFrom" className="text-sm font-medium mb-2 block">
            Visited From
          </Label>
          <Input
            id="visitedFrom"
            type="date"
            value={value.visitedFrom || ''}
            onChange={(e) => handleFilterChange({
              visitedFrom: e.target.value || undefined,
            })}
            className="h-9"
          />
        </div>
        <div>
          <Label htmlFor="visitedTo" className="text-sm font-medium mb-2 block">
            Visited To
          </Label>
          <Input
            id="visitedTo"
            type="date"
            value={value.visitedTo || ''}
            onChange={(e) => handleFilterChange({
              visitedTo: e.target.value || undefined,
            })}
            className="h-9"
          />
        </div>
        <p className="text-[10px] text-text-muted col-span-2 -mt-2">
          Patients who had any visit within this date range
        </p>
      </div>

      {/* Spending Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="minTotalSpent" className="text-sm font-medium mb-2 block">
            Min Total Spent (RM)
          </Label>
          <Input
            id="minTotalSpent"
            type="number"
            min={0}
            placeholder="e.g. 100"
            value={value.minTotalSpent || ''}
            onChange={(e) => handleFilterChange({
              minTotalSpent: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
        </div>
        <div>
          <Label htmlFor="maxTotalSpent" className="text-sm font-medium mb-2 block">
            Max Total Spent (RM)
          </Label>
          <Input
            id="maxTotalSpent"
            type="number"
            min={0}
            placeholder="e.g. 1000"
            value={value.maxTotalSpent || ''}
            onChange={(e) => handleFilterChange({
              maxTotalSpent: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
        </div>
      </div>

      {/* Demographics Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="gender" className="text-sm font-medium mb-2 block">
            Gender
          </Label>
          <Select
            value={value.gender || 'all'}
            onValueChange={(v) => handleFilterChange({
              gender: v === 'all' ? undefined : v as 'Male' | 'Female',
            })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Male">Male</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="birthdayMonth" className="text-sm font-medium mb-2 block">
            Birthday Month
          </Label>
          <Select
            value={value.birthdayMonth?.toString() || 'all'}
            onValueChange={(v) => handleFilterChange({
              birthdayMonth: v === 'all' ? undefined : Number(v),
            })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              <SelectItem value="1">January</SelectItem>
              <SelectItem value="2">February</SelectItem>
              <SelectItem value="3">March</SelectItem>
              <SelectItem value="4">April</SelectItem>
              <SelectItem value="5">May</SelectItem>
              <SelectItem value="6">June</SelectItem>
              <SelectItem value="7">July</SelectItem>
              <SelectItem value="8">August</SelectItem>
              <SelectItem value="9">September</SelectItem>
              <SelectItem value="10">October</SelectItem>
              <SelectItem value="11">November</SelectItem>
              <SelectItem value="12">December</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Age Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ageMin" className="text-sm font-medium mb-2 block">
            Min Age
          </Label>
          <Input
            id="ageMin"
            type="number"
            min={0}
            max={120}
            placeholder="e.g. 25"
            value={value.ageMin || ''}
            onChange={(e) => handleFilterChange({
              ageMin: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
        </div>
        <div>
          <Label htmlFor="ageMax" className="text-sm font-medium mb-2 block">
            Max Age
          </Label>
          <Input
            id="ageMax"
            type="number"
            min={0}
            max={120}
            placeholder="e.g. 45"
            value={value.ageMax || ''}
            onChange={(e) => handleFilterChange({
              ageMax: e.target.value ? Number(e.target.value) : undefined,
            })}
            className="h-9"
          />
        </div>
      </div>

      {/* Preview Section */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-sm font-medium">Audience Preview</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshPreview}
            disabled={previewMutation.isPending}
            className="h-7 px-2"
          >
            {previewMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
          </Button>
        </div>

        {previewMutation.isPending && !preview ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : preview ? (
          <>
            {/* Count badge */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 mb-3">
              <Users className="w-5 h-5 text-primary" />
              <div>
                <span className="text-lg font-semibold text-text-dark">
                  {preview.totalCount.toLocaleString()}
                </span>
                <span className="text-sm text-text-muted ml-2">
                  patients match criteria
                </span>
              </div>
            </div>

            {/* Sample contacts */}
            {preview.sampleContacts.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[11px] text-text-muted">Sample recipients:</p>
                {preview.sampleContacts.slice(0, 3).map((contact) => (
                  <div
                    key={contact.contactId}
                    className="flex items-center justify-between p-2 rounded-md bg-muted/30 text-xs"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-text-dark">
                        {contact.patientName}
                      </span>
                      <span className="text-text-muted ml-2">
                        ({contact.patientPno})
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[10px] ml-2">
                      {contact.branchName}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {preview.totalCount === 0 && (
              <p className="text-sm text-text-muted text-center py-2">
                No patients match the criteria
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-text-muted text-center py-2">
            Set filters to preview audience
          </p>
        )}
      </div>
    </div>
  )
}
